/** The stain rules: how a layer is compared against the layers behind it. */

export const RULE_IDS = {
  diff: 0,
  similarity: 1,
  signed: 2,
  threshold: 3,
  persist: 4,
  flow: 5,
  edge: 6,
  background: 7,
  chroma: 8,
  novelty: 9,
  unrest: 10,
  custom: 11,
} as const;

export type RuleKey = keyof typeof RULE_IDS;

export interface RuleDef {
  key: RuleKey;
  id: number;
  label: string;
  blurb: string;
  /** Parameter defaults applied when the rule is selected. */
  suggests?: Partial<{
    gain: number;
    bias: number;
    threshold: number;
    softness: number;
    decay: number;
    gamma: number;
    colormap: number;
    blur: number;
    compare: number;
    stride: number;
  }>;
}

export const RULES: RuleDef[] = [
  {
    key: 'diff',
    id: RULE_IDS.diff,
    label: 'Difference',
    blurb: 'Stains wherever this layer departs from the one behind it. The plain motion-energy read.',
    suggests: { gain: 4, gamma: 1, colormap: 0 },
  },
  {
    key: 'similarity',
    id: RULE_IDS.similarity,
    label: 'Similarity',
    blurb: 'The inverse: stains what held still. Pair with the Similarity density mode to dissolve movers.',
    suggests: { gain: 6, gamma: 2, colormap: 9 },
  },
  {
    key: 'signed',
    id: RULE_IDS.signed,
    label: 'Signed change',
    blurb: 'Direction of luminance change — warm where the frame brightened, cool where it darkened.',
    suggests: { gain: 6, gamma: 1, colormap: 6 },
  },
  {
    key: 'threshold',
    id: RULE_IDS.threshold,
    label: 'Threshold',
    blurb: 'Hard binary stain above a difference cutoff. Cleanest silhouettes of what moved.',
    suggests: { gain: 1, threshold: 0.06, softness: 0.02, colormap: 8 },
  },
  {
    key: 'persist',
    id: RULE_IDS.persist,
    label: 'Persistence trail',
    blurb: 'A decaying running maximum, so a moving subject drags a comet tail through t.',
    suggests: { gain: 3, decay: 0.94, gamma: 1, colormap: 1 },
  },
  {
    key: 'flow',
    id: RULE_IDS.flow,
    label: 'Flow direction',
    blurb: 'One-step optical flow. Hue is the direction of travel, opacity its speed.',
    suggests: { gain: 3, blur: 1, colormap: 7 },
  },
  {
    key: 'edge',
    id: RULE_IDS.edge,
    label: 'Moving edges',
    blurb: 'Spatial edges gated by temporal change: contours only where they are actually travelling.',
    suggests: { gain: 3, blur: 1, colormap: 5 },
  },
  {
    key: 'background',
    id: RULE_IDS.background,
    label: 'Background subtract',
    blurb: 'Distance from a running average of all earlier layers. Isolates foreground from a static plate.',
    suggests: { gain: 3, colormap: 2, compare: 2 },
  },
  {
    key: 'chroma',
    id: RULE_IDS.chroma,
    label: 'Chroma shift',
    blurb: 'Hue change with brightness divided out — catches color moves that luminance diffing misses.',
    suggests: { gain: 3, colormap: 3 },
  },
  {
    key: 'novelty',
    id: RULE_IDS.novelty,
    label: 'Novelty vs first',
    blurb: 'Every layer measured against layer 0. Shows cumulative drift away from the opening frame.',
    suggests: { gain: 2.5, colormap: 4, compare: 1 },
  },
  {
    key: 'unrest',
    id: RULE_IDS.unrest,
    label: 'Unrest',
    blurb: 'Rewards places that keep changing over many layers instead of flickering once.',
    suggests: { gain: 3, decay: 0.9, colormap: 1 },
  },
  {
    key: 'custom',
    id: RULE_IDS.custom,
    label: 'Custom GLSL',
    blurb: 'Write the comparison yourself. Compiled into the stain pass on Apply.',
  },
];

export const COMPARE_TARGETS = [
  { value: 0, label: 'previous layer (t - stride)' },
  { value: 1, label: 'first layer (t = 0)' },
  { value: 2, label: 'running mean (background)' },
  { value: 3, label: 'next layer (t + stride)' },
  { value: 4, label: 'symmetric (t ± stride)' },
];

export const COLORMAPS = [
  { value: 0, label: 'turbo' },
  { value: 1, label: 'inferno' },
  { value: 2, label: 'magma' },
  { value: 3, label: 'plasma' },
  { value: 4, label: 'viridis' },
  { value: 5, label: 'grayscale' },
  { value: 6, label: 'fire / ice (signed)' },
  { value: 7, label: 'hue wheel' },
  { value: 8, label: 'amber' },
  { value: 9, label: 'ice' },
  { value: 10, label: 'acid' },
];

export const DEFAULT_CUSTOM_RULE = `// Stain = vec4(rgb, density). Returned density drives opacity.
// Here: warm where the frame brightened, cool where it darkened,
// but only for pixels sitting on a moving edge.
float change = f.dluma * p.gain;
float onEdge = smoothstep(0.02, 0.18, f.edge);
float density = clamp(abs(change) * onEdge, 0.0, 1.0);
vec3 warm = vec3(1.0, 0.45, 0.15);
vec3 cool = vec3(0.25, 0.6, 1.0);
vec3 rgb = mix(cool, warm, step(0.0, change));
return vec4(rgb, pow(density, p.gamma));`;

export const CUSTOM_RULE_REFERENCE = `f.cur / f.prev / f.mean / f.first — vec3 colors
f.delta — cur - prev, per channel
f.dmag — 0..1 magnitude of the difference
f.dluma — signed luminance change
f.peak — decayed running max of dmag
f.grad — spatial luminance gradient (vec2)
f.flow — one-step flow estimate (vec2)
f.edge — length(f.grad)
f.uv / f.layer / f.t01 — position in x,y,t
p.gain p.bias p.threshold p.softness
p.decay p.gamma p.a p.b p.c — panel sliders`;

export function ruleByKey(key: RuleKey): RuleDef {
  const rule = RULES.find((r) => r.key === key);
  if (!rule) throw new Error(`Unknown rule: ${key}`);
  return rule;
}
